import { useCallback, useEffect, useState } from 'react'
import { useDisclosure, useToast } from '@chakra-ui/react'
import { useAppDispatch, useAppSelector } from '@/store'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { fetchGroups, deleteGroup, fetchGroupMembers } from '@/store/slices/groupsSlice'
import { Group, GroupMember } from '@/types'

interface UseGroupsReturn {
  groups: Group[]
  membersById: Record<number, GroupMember[]>
  isLoading: boolean
  isAdmin: boolean
  selectedGroup: Group | null
  createModal: ReturnType<typeof useDisclosure>
  editModal: ReturnType<typeof useDisclosure>
  deleteModal: ReturnType<typeof useDisclosure>
  membersModal: ReturnType<typeof useDisclosure>
  handleEdit: (group: Group) => void
  handleDelete: (group: Group) => void
  handleMembers: (group: Group) => void
  confirmDelete: () => Promise<void>
}

export function useGroups(): UseGroupsReturn {
  const { t } = useAppTranslation(['admin'])
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { user } = useAuth()

  const { list: groups, isLoading, membersById } = useAppSelector((state) => state.groups)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'super'

  const createModal = useDisclosure()
  const editModal = useDisclosure()
  const deleteModal = useDisclosure()
  const membersModal = useDisclosure()

  useEffect(() => {
    dispatch(fetchGroups())
  }, [dispatch])

  useEffect(() => {
    groups.forEach((group) => {
      if (!membersById[group.id]) {
        dispatch(fetchGroupMembers(group.id))
      }
    })
  }, [dispatch, groups, membersById])

  const handleEdit = useCallback(
    (group: Group) => {
      setSelectedGroup(group)
      editModal.onOpen()
    },
    [editModal]
  )

  const handleDelete = useCallback(
    (group: Group) => {
      setSelectedGroup(group)
      deleteModal.onOpen()
    },
    [deleteModal]
  )

  const handleMembers = useCallback(
    (group: Group) => {
      setSelectedGroup(group)
      membersModal.onOpen()
    },
    [membersModal]
  )

  const confirmDelete = useCallback(async () => {
    if (!selectedGroup) return

    try {
      await dispatch(deleteGroup(selectedGroup.id)).unwrap()
      toast({
        title: t('groups.deleteSuccess', { ns: 'admin' }),
        status: 'success',
        duration: 2000,
      })
      deleteModal.onClose()
      setSelectedGroup(null)
    } catch {
      toast({
        title: t('groups.deleteFailed', { ns: 'admin' }),
        status: 'error',
        duration: 3000,
      })
    }
  }, [selectedGroup, dispatch, toast, t, deleteModal])

  return {
    groups,
    membersById,
    isLoading,
    isAdmin,
    selectedGroup,
    createModal,
    editModal,
    deleteModal,
    membersModal,
    handleEdit,
    handleDelete,
    handleMembers,
    confirmDelete,
  }
}

export default useGroups
